import { createContext, forwardRef, useContext, useMemo } from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Icon } from '../../atoms/icon/icon'

const DEFAULT_VARIANT = 'info' as const
const DEFAULT_SIZE = 'md' as const

/* Ømni Alerts (Figma node 2:1695). Anatomy: [icon? title rightIcon?] over a
   full-width body, with a dismiss control top-aligned to the title row.

   Icon boxes are not sized in px: --alert-icon-box carries the line-height of
   the text row the icons sit on (title tier per size; description tier when
   the alert renders a description without a title), so they stay flush with
   the first text line and keep scaling with the responsive type matrix.

   Grid geometry is resolved in CSS from the rendered DOM (:has / sibling
   selectors), not from props: with a right icon the title column hugs its
   text (max-content, capped by the available space) so the icon sits one gap
   after it, and the trailing 1fr keeps a full-span description at full
   width. Row-1 items carry explicit placement because grid auto-flow would
   send the right icon below a full-span description. */
const alertVariants = tv(
  {
    slots: {
      /* Links keep the acqua link blue and weight whatever the variant,
         mirroring the TextLink atom treatment (!decoration-from-font
         outspecifies the apps' RootLayout `& a` shorthand). */
      root: 'font-body shadow-sticky-nav [&_a]:text-link-fresh flex w-full items-start gap-4 rounded-md border px-4 py-2 font-semibold [&_a]:font-semibold [&_a]:underline [&_a]:!decoration-from-font [&_a]:[text-decoration-skip-ink:none] [&_a]:[text-underline-position:from-font]',
      content:
        'grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)] gap-x-2 gap-y-1 has-[[data-slot=alert-icon]]:grid-cols-[auto_minmax(0,1fr)] has-[[data-slot=alert-icon]]:has-[[data-slot=alert-right-icon]]:grid-cols-[auto_minmax(0,max-content)_minmax(0,1fr)] has-[[data-slot=alert-right-icon]]:grid-cols-[minmax(0,max-content)_minmax(0,1fr)]',
      /* Shared by the left/right icons and the dismiss button; keeps each
         acqua Icon glyph equal to the box. */
      iconBox:
        'text-(length:--alert-icon-box) flex shrink-0 items-center justify-center leading-none [&>svg]:size-[1em] [&_[data-slot=icon]]:text-[1em]',
      title:
        'col-start-1 row-start-1 min-w-0 break-words [[data-slot=alert-icon]~&]:col-start-2',
      /* Spans the full row (flush under the left icon, per Figma) only when a
         title precedes it — a lone description stays beside the icon. */
      description: 'break-words [[data-slot=alert-title]~&]:col-span-full',
      close:
        'focus-visible:ring-primary-200 cursor-pointer transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
    },
    variants: {
      variant: {
        info: {
          root: 'bg-information-bg border-information-stroke text-information-title',
          iconBox: 'text-information-icon',
          title: 'text-information-title',
          description: 'text-information-body'
        },
        success: {
          root: 'bg-success-bg border-success-stroke text-success-title',
          iconBox: 'text-success-icon',
          title: 'text-success-title',
          description: 'text-success-body'
        },
        warning: {
          root: 'bg-warning-bg border-warning-stroke text-warning-title',
          iconBox: 'text-warning-icon',
          title: 'text-warning-title',
          description: 'text-warning-body'
        },
        danger: {
          root: 'bg-danger-bg border-danger-stroke text-danger-title',
          iconBox: 'text-danger-icon',
          title: 'text-danger-title',
          description: 'text-danger-body'
        }
      },
      size: {
        md: {
          root: 'text-body-md has-[[data-slot=alert-description]]:not-has-[[data-slot=alert-title]]:[--alert-icon-box:var(--text-caption-lg--line-height)] [--alert-icon-box:var(--text-body-md--line-height)]',
          description: 'text-caption-lg font-normal'
        },
        sm: {
          root: 'text-caption-lg has-[[data-slot=alert-description]]:not-has-[[data-slot=alert-title]]:[--alert-icon-box:var(--text-caption-md--line-height)] [--alert-icon-box:var(--text-caption-lg--line-height)]',
          description: 'text-caption-md'
        }
      }
    },
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      size: DEFAULT_SIZE
    }
  },
  /* tv's bundled tailwind-merge doesn't know the Ømni text tokens, so it
     classifies `text-caption-*` as a text color and drops the variant's
     real color class. Merging is owned by the cn() below instead, whose
     registry maps the Ømni tokens to the font-size group. */
  { twMerge: false }
)

export type AlertVariant = NonNullable<
  VariantProps<typeof alertVariants>['variant']
>
export type AlertSize = NonNullable<VariantProps<typeof alertVariants>['size']>

/** @deprecated Use `danger`. Kept so existing `variant='error'` callers compile. */
export type LegacyAlertVariant = 'error'

const AlertVariantContext = createContext<{
  variant: AlertVariant
  size: AlertSize
}>({
  variant: DEFAULT_VARIANT,
  size: DEFAULT_SIZE
})

export type AlertProps = React.ComponentPropsWithRef<'div'> &
  Omit<VariantProps<typeof alertVariants>, 'variant'> & {
    variant?: AlertVariant | LegacyAlertVariant
    icon?: React.ReactNode
    rightIcon?: React.ReactNode
    /** Renders the dismiss button when provided. */
    onClose?: () => void
    /** Accessible name for the dismiss button. */
    closeLabel?: string
  }

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      variant,
      size,
      icon,
      rightIcon,
      onClose,
      closeLabel = 'Dismiss',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const resolvedVariant: AlertVariant =
      variant === 'error' ? 'danger' : variant ?? DEFAULT_VARIANT
    const resolvedSize: AlertSize = size ?? DEFAULT_SIZE
    const { root, content, iconBox, close } = alertVariants({
      variant: resolvedVariant,
      size: resolvedSize
    })
    const contextValue = useMemo(
      () => ({ variant: resolvedVariant, size: resolvedSize }),
      [resolvedVariant, resolvedSize]
    )
    return (
      <AlertVariantContext.Provider value={contextValue}>
        <div
          ref={ref}
          role='alert'
          data-slot='alert'
          data-variant={resolvedVariant}
          data-size={resolvedSize}
          className={cn(root(), className)}
          {...props}
        >
          <div data-slot='alert-content' className={content()}>
            {icon && (
              <span
                data-slot='alert-icon'
                className={cn(iconBox(), 'col-start-1 row-start-1')}
              >
                {icon}
              </span>
            )}
            {children}
            {rightIcon && (
              <span
                data-slot='alert-right-icon'
                className={cn(
                  iconBox(),
                  'col-start-2 row-start-1 justify-self-start [[data-slot=alert-icon]~&]:col-start-3'
                )}
              >
                {rightIcon}
              </span>
            )}
          </div>
          {onClose && (
            <button
              type='button'
              data-slot='alert-close'
              aria-label={closeLabel}
              onClick={onClose}
              className={cn(iconBox(), close())}
            >
              <Icon name='close' />
            </button>
          )}
        </div>
      </AlertVariantContext.Provider>
    )
  }
)
Alert.displayName = 'Alert'

/* ------------------------------------------------------------------ */
/*  AlertTitle                                                         */
/* ------------------------------------------------------------------ */

export type AlertTitleProps = React.ComponentPropsWithRef<'div'>

export const AlertTitle = forwardRef<HTMLDivElement, AlertTitleProps>(
  ({ className, ...props }, ref) => {
    const { title } = alertVariants(useContext(AlertVariantContext))
    return (
      <div
        ref={ref}
        data-slot='alert-title'
        className={cn(title(), className)}
        {...props}
      />
    )
  }
)
AlertTitle.displayName = 'AlertTitle'

/* ------------------------------------------------------------------ */
/*  AlertDescription                                                   */
/* ------------------------------------------------------------------ */

export type AlertDescriptionProps = React.ComponentPropsWithRef<'div'>

export const AlertDescription = forwardRef<
  HTMLDivElement,
  AlertDescriptionProps
>(({ className, ...props }, ref) => {
  const { description } = alertVariants(useContext(AlertVariantContext))
  return (
    <div
      ref={ref}
      data-slot='alert-description'
      className={cn(description(), className)}
      {...props}
    />
  )
})
AlertDescription.displayName = 'AlertDescription'
